import React from "react";
import Image from "next/image";
import type { IAvis } from "@/types/testimonial";

interface AvisCardProps {
  avis: IAvis;
}

const MAX_STARS = 5;

/**
 * Compact white review card.
 * Fixed to 771 × 323 on desktop (design spec); fluid + auto-height on mobile.
 */
const AvisCard: React.FC<AvisCardProps> = ({ avis }) => {
  const { name, rating, date, textBold, text } = avis;
  const filledStars = Math.round(rating);

  return (
    <article className="flex w-full flex-col rounded-2xl border border-[#EFEFEF] bg-white px-6 pt-4.5 pb-3.75 transition-transform duration-300 ease-out hover:translate-y-6 md:h-[323px]">
      {/* Header: name + rating */}
      <div className="flex items-start justify-between gap-2">
        <h4 className="text-[14px] leading-5 text-[#1B365E]">{name}</h4>

        <div className="flex flex-col items-end">
          <div
            className="flex gap-1"
            role="img"
            aria-label={`Note de ${rating} sur ${MAX_STARS}`}
          >
            {Array.from({ length: MAX_STARS }).map((_, index) => (
              <Image
                key={index}
                src="/assets/icons/star.svg"
                alt=""
                aria-hidden="true"
                width={16}
                height={16}
                className={`w-4 shrink-0 ${index < filledStars ? "" : "opacity-30"}`}
              />
            ))}
          </div>
          <span className="text-right text-[8px] leading-5 text-[#40BFDE]">
            {date}
          </span>
        </div>
      </div>

      {/* Highlighted summary */}
      <p className="mt-9 text-xs font-bold leading-5 text-[#1B365E]">
        {textBold}
      </p>

      {/* Body */}
      <p className="mt-4 line-clamp-7 text-xs leading-5 text-[#1B365E]">
        {text}
      </p>
    </article>
  );
};

export default AvisCard;
